import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@core/store';

interface GuestGuardProps {
    children: ReactNode;
}

/**
 * Guest Guard Component
 *
 * Prevents authenticated users from accessing guest-only routes
 * (like login and register pages).
 * Redirects authenticated users to their intended destination or home.
 */
export function GuestGuard({ children }: GuestGuardProps) {
    const location = useLocation();
    const { isUserAuthenticated, isLoading } = useAppSelector((state) => state.auth);

    // Show nothing while checking auth state
    if (isLoading) {
        return null;
    }

    // Redirect authenticated customers (admins remain "guests" on customer-facing routes)
    if (isUserAuthenticated) {
        // Get the intended destination from location state
        const from = (location.state as { from?: Location })?.from?.pathname;
        return <Navigate to={from ?? '/'} replace />;
    }

    return <>{children}</>;
}

export default GuestGuard;
